import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';

// Animations
const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
    opacity: 1;
  }
  to {
    transform: translateX(100%);
    opacity: 0;
  }
`;

// Styled components
const ToastContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 1000;
  animation: ${props => props.$leaving ? slideOut : slideIn} 0.3s ease-in-out forwards;
  max-width: 300px;
`;

const ToastContent = styled.div`
  background-color: ${props => props.type === 'success' ? '#4CAF50' : 
    props.type === 'error' ? '#F44336' : 
    props.type === 'warning' ? '#FF9800' : '#2196F3'};
  color: white;
  padding: 12px 16px;
  border-radius: 4px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
`;

const Message = styled.span`
  flex: 1;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  margin-left: 12px;
  cursor: pointer;
  font-size: 18px;
  opacity: 0.8;
  
  &:hover {
    opacity: 1;
  }
`;

const ProgressBar = styled.div`
  height: 3px;
  background-color: rgba(255, 255, 255, 0.7);
  position: absolute;
  bottom: 0;
  left: 0;
  width: ${props => props.$progress}%;
  transition: width linear;
`;

/**
 * Simple toast notification component with slide animation
 */
const SimpleToast = ({ 
  message, 
  type = 'success', 
  duration = 3000, 
  onClose 
}) => {
  const [progress, setProgress] = React.useState(100);
  const [leaving, setLeaving] = React.useState(false);
  
  useEffect(() => {
    // Set up progress bar animation
    const startTime = Date.now();
    const endTime = startTime + duration;
    
    const updateProgress = () => {
      const now = Date.now();
      const remaining = Math.max(0, endTime - now);
      const newProgress = (remaining / duration) * 100;
      
      setProgress(newProgress);
      
      if (remaining > 0) {
        requestAnimationFrame(updateProgress);
      } else {
        handleClose();
      }
    };
    
    const animationId = requestAnimationFrame(updateProgress);
    
    return () => {
      cancelAnimationFrame(animationId);
    };
  }, [duration]);
  
  const handleClose = () => {
    setLeaving(true);
    // Wait for animation to finish before actually removing
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  };
  
  return (
    <ToastContainer $leaving={leaving}>
      <ToastContent type={type}>
        <Message>{message}</Message>
        <CloseButton onClick={handleClose}>&times;</CloseButton>
        <ProgressBar $progress={progress} />
      </ToastContent>
    </ToastContainer>
  );
};

export default SimpleToast;
